// /src/pages/ContactPage.jsx
import React from 'react';
import { Check, Shield, Server, GraduationCap, FileText, Github, Calendar } from 'lucide-react';
import { cardCls, headingCls, bodyText, mutedText, ctaPrimary, pageHeading, sectionLabel, pageWrap, sectionInner } from '../components/styles';

export default function ContactPage() {
  return (
    <div className={pageWrap}>
      {/* Hero */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl animate-stagger">
            <h1 className={`${pageHeading} mb-4`}><span className="opacity-50">$</span> book-me</h1>
            <p className={`text-xl ${mutedText} max-w-2xl`}>
              Not a vendor. Not an auditor reading from a checklist. I build the tools I use on engagements
              and I've administered the same federal networks I now help secure.
            </p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Why Me - asymmetric */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// why me'}</h2>
            <div className="grid md:grid-cols-[1.2fr_0.8fr_1fr] gap-6 animate-stagger">
              <div className={`${cardCls} p-6`}>
                <Server className="text-[var(--accent)] mb-3" size={32} />
                <h4 className={`font-bold ${headingCls} mb-2`}>Federal Experience</h4>
                <p className={`text-sm ${mutedText}`}>
                  Former IRS Network & Systems Admin contractor. I've managed the infrastructure I now test.
                </p>
              </div>
              <div className={`${cardCls} p-6`}>
                <Shield className="text-[var(--accent-2)] mb-3" size={32} />
                <h4 className={`font-bold ${headingCls} mb-2`}>Builder, Not Just Tester</h4>
                <p className={`text-sm ${mutedText}`}>
                  HADES (153k lines, Kubernetes) and VitalChain (Hyperledger Fabric, AWS pilot). I ship production platforms.
                </p>
              </div>
              <div className={`${cardCls} p-6`}>
                <GraduationCap className="text-[var(--accent)] mb-3" size={32} />
                <h4 className={`font-bold ${headingCls} mb-2`}>Academic Rigor</h4>
                <p className={`text-sm ${mutedText}`}>
                  BS Cybersecurity (3.88 GPA), Graduate Certificate in Blockchain, MBA in Healthcare Administration. All from Champlain College.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Services */}
      <section className="py-10" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// services'}</h2>
            <div className="space-y-8 animate-stagger">
              {[
                {
                  name: 'Threat Assessment',
                  duration: '90-minute deep-dive',
                  description:
                    'I review your infrastructure, map your attack surface, and tell you where you\'re actually exposed, not where a scan says you might be. Focused on healthcare-specific threats, HIPAA gaps, and compliance blind spots.',
                  includes: ['Attack surface mapping', 'HIPAA/compliance gap analysis', 'Prioritized risk register', 'Written report with remediation steps']
                },
                {
                  name: 'Security Engagement',
                  duration: '2-6 weeks',
                  description:
                    'Full-scope pen testing, security audits, or incident response. I use my own tooling (HADES metadata forensics) alongside standard frameworks. You get findings that are specific, reproducible, and actionable, not a 200-page PDF of scanner output.',
                  includes: ['Network & application penetration testing', 'HADES metadata forensics scan', 'Compliance review (HIPAA, FERPA, CJIS)', 'Remediation roadmap with owner assignments', 'Executive briefing for leadership']
                },
                {
                  name: 'Workshop & Training',
                  duration: 'Half-day or full-day',
                  description:
                    'Hands-on training built from real engagements, not slide decks. I\'ve trained corrections officers, IT teams, and healthcare staff. I know how to teach people who aren\'t security-first.',
                  includes: ['Custom curriculum for your team\'s level', 'Live labs with real attack scenarios', 'Playbooks & reference materials to keep', 'Follow-up office hours']
                }
              ].map((service, i) => (
                <div key={i} className={`${cardCls} p-8 hover:translate-y-[-2px] transition`}>
                  <div className="mb-6">
                    <h3 className={`text-3xl ${headingCls} mb-2`}>{service.name}</h3>
                    <p className="font-mono text-sm text-[var(--accent)]">{service.duration}</p>
                  </div>

                  <p className={`${bodyText} text-lg mb-6 leading-relaxed`}>{service.description}</p>

                  <div className="rounded-lg p-6 mb-6 border border-[color:var(--border)] bg-[var(--bg2)]">
                    <h4 className={`font-bold ${headingCls} mb-3`}>What's included:</h4>
                    <ul className="space-y-2">
                      {service.includes.map((item, j) => (
                        <li key={j} className={`flex items-center gap-2 ${bodyText}`}>
                          <Check className="text-[var(--accent-2)]" size={20} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a href="https://calendly.com/dlaurenzi" target="_blank" rel="noopener noreferrer" className={`${ctaPrimary} w-full justify-center text-base`}>
                    <Calendar size={24} /> Book a call
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Calendly Embed */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// schedule'}</h2>
            <div className={`${cardCls} p-8`}>
              <div className="mb-6">
                <h3 className={`text-2xl ${headingCls} mb-2`}>Pick a time that works</h3>
                <p className={mutedText}>
                  Select a slot below or <a href="mailto:dlaurenzi@domeniclaurenzi.com" className="text-[var(--accent)] hover:opacity-80 transition">email me</a> if you don't see one that fits.
                </p>
              </div>
              <div
                className="rounded-lg overflow-hidden"
                style={{ minHeight: '660px' }}
              >
                <iframe
                  src="https://calendly.com/dlaurenzi?hide_gdpr_banner=1"
                  width="100%"
                  height="660"
                  frameBorder="0"
                  title="Schedule a call with Domenic Laurenzi"
                  style={{ border: 'none', minWidth: '320px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* FAQ */}
      <section className="py-10" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// faq'}</h2>
            <div
              className="rounded-lg p-10 border border-[color:var(--border)]"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.85), rgba(0,0,0,0.6))' }}
            >
              <h3 className="text-3xl font-bold text-[var(--text)] mb-6">Frequently Asked Questions</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-bold text-[var(--text)] mb-2">What industries do you serve?</h4>
                  <p className={mutedText}>
                    Healthcare (hospitals, clinics, EHR vendors), education (K-12, higher ed), and government (federal
                    contractors, state agencies). Deep expertise in HIPAA, FERPA, and CJIS compliance.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-[var(--text)] mb-2">Do you travel for on-site work?</h4>
                  <p className={mutedText}>
                    Yes, available for on-site security assessments and training throughout the Northeast. Remote work available
                    nationwide.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-[var(--text)] mb-2">Typical engagement timeline?</h4>
                  <p className={mutedText}>Consultations within 1 week. Full engagements typically 2-6 weeks depending on scope.</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-[var(--text)] mb-2">What makes you different from a larger firm?</h4>
                  <p className={mutedText}>
                    You talk directly to the person doing the work. No account managers, no junior analysts running automated scans.
                    I built a 153k-line forensics platform and deployed a blockchain pilot on AWS. That's the level of depth I bring to every engagement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* By the Numbers */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// by the numbers'}</h2>
            <div className={`${cardCls} p-8`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <FileText className="text-[var(--accent)] mb-2" size={24} />
                  <div className="metric-value text-2xl">83</div>
                  <div className={`text-sm ${mutedText} mt-1`}>Blog Posts</div>
                </div>
                <div>
                  <Shield className="text-[var(--accent-2)] mb-2" size={24} />
                  <div className="metric-value text-2xl">2</div>
                  <div className={`text-sm ${mutedText} mt-1`}>Production Platforms</div>
                </div>
                <div>
                  <GraduationCap className="text-[var(--accent)] mb-2" size={24} />
                  <div className="metric-value text-2xl">3.88</div>
                  <div className={`text-sm ${mutedText} mt-1`}>GPA, BS Cybersecurity</div>
                </div>
                <div>
                  <Github className="text-[var(--accent-2)] mb-2" size={24} />
                  <div className="metric-value text-2xl">7+</div>
                  <div className={`text-sm ${mutedText} mt-1`}>Open-Source Projects</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
